import { MalaPuscicaDol } from 'kzv-icons';
import { uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';

import Button from '../buttons/Button';
import PoiCard from 'components/cards/poi/PoiCard';
import LoadingSpinner from '../LoadingSpinner';
import ResultsSkeletonLoader from './ResultsSkeletonLoader';
import { Mixpanel } from 'utils/Mixpanel';
import Area from 'models/Area';
import Category from 'models/Category';

const SearchNearbyPois = ({
    area,
    category,
    exclude = []
}: {
    area: Area;
    category?: Category;
    exclude?: number[] | string[];
}) => {
    const [page, setPage] = useState(1);
    const [loadedArea, setLoadedArea] = useState<Area>();
    const [loadedCategory, setLoadedCategory] = useState<Category>();
    const [loadedPois, setLoadedPois] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, last: null });
    const endpoint = '/public/pois/nearby';

    const params = {
        lat: area?.centroid?.coordinates[1].toString(),
        lng: area?.centroid?.coordinates[0].toString(),
        'page[size]': '20',
        'page[number]': page.toString(),
        include: 'thumbs,type,areas'
    };

    if (exclude.length > 0) {
        params['exclude'] = exclude.join(',');
    }

    if (category) {
        params['filter[categories.slug]'] = category.slug;
    }

    const query = `${new URLSearchParams(params).toString()}`;
    const { data: nearbyPois, isValidating } = useSWR(endpoint + '?' + query);

    useEffect(() => {
        if (nearbyPois) {
            if (area.slug !== loadedArea?.slug || category?.slug !== loadedCategory?.slug) {
                setLoadedPois([]);
                setLoadedArea(area);
                setLoadedCategory(category);
            }
            const data = nearbyPois.data;
            setLoadedPois((existing) => uniqBy([...existing, ...data], (poi) => poi.id));
            setPagination({ last: data.last_page, current: data.current_page });
        }
    }, [nearbyPois, area, category, loadedArea, loadedCategory]);

    const loadMore = () => {
        Mixpanel.track('Search load more nearby pois');
        setPage(page + 1);
    };

    return (
        <div className="nearby mt-6 container">
            <h1 className="text-center">Ostale ideje v bližini</h1>
            {loadedPois.length === 0 && <ResultsSkeletonLoader />}

            {loadedPois.length > 0 && (
                <>
                    <div
                        className={`mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3  xl:grid-cols-4 xl:gap-10 3xl:grid-cols-5 3xl:gap-14`}
                    >
                        {loadedPois &&
                            loadedPois.map((poi) => {
                                return (
                                    <div className="justify-self-center w-full" key={poi.id}>
                                        <PoiCard
                                            prefetch={false}
                                            images={poi.thumbs}
                                            title={poi.name}
                                            link={`/ideja/${poi.slug}`}
                                            location={
                                                poi.municipality ? `${poi.municipality?.name}, ${poi.region?.name}` : ''
                                            }
                                            poiType={poi.type}
                                            attributes={poi.poi_attributes}
                                            category={category}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                    <div className="text-center mt-6">
                        <Button
                            disabled={isValidating}
                            color="secondary"
                            endIcon={isValidating ? <LoadingSpinner /> : <MalaPuscicaDol strokeWidth={2} />}
                            onClick={loadMore}
                            outerStyles={pagination.current >= pagination.last ? 'invisible' : ''}
                        >
                            Naloži več
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

SearchNearbyPois.propTypes = {};

export default SearchNearbyPois;
